/**
 * @fileoverview Actions to manage campaigns in the inbox of Mercately.
 * Provides functions to load campaigns from the CampaignsModelMC model and update the global state.
 * @module actions/inbox/campaignsActions
 * @category Actions Layer
 * @since 2.0.0
 * @version 3.1.0
 */

/**
 * @typedef {Object} CampaignsParams
 * @description Parameters for the query of campaigns.
 * @property {string} [status] - Campaign status (example: 'active', 'paused').
 * @property {number} [limit] - Limit of results to return.
 * @property {number} [offset] - Offset for pagination.
 */
import CampaignsModelMC from '../../presentation/models/CampaignsModelMC';

/**
 * Action to get campaigns always, avoiding duplication if already loading.
 *
 * Implements the action layer pattern, managing the loading and error states.
 *
 * @function fetchCampaignsAlways
 * @memberof module:actions/inbox/campaignsActions
 * @param {CampaignsParams} [params={}] - Parameters for the query of campaigns.
 * @returns {Function} Function thunk for dispatch and getState.
 * @throws {Error} If there is an error in the query to the model.
 * @example
 * dispatch(fetchCampaignsAlways({ status: 'active', limit: 10 }));
 * @see CampaignsModelMC.getCampaigns
 */
export const fetchCampaignsAlways = (params = {}) => async (dispatch, getState) => {
  const { loadingCampaigns } = getState().mainReducer || {};
  if (loadingCampaigns) return;
  dispatch({ type: "START_CAMPAIGNS_REQUEST" });
  try {
    const data = await CampaignsModelMC.getCampaigns(params);
    dispatch({ type: "SET_CAMPAIGNS", data });
  } catch (err) {
    dispatch({ type: "SET_CAMPAIGNS_ERROR", err });
  }
};

/**
 * Action to get campaigns only if the state is empty and not loading.
 *
 * Implements the action layer pattern, optimizing the loading to avoid unnecessary calls.
 *
 * @function fetchCampaignsIfEmpty
 * @memberof module:actions/inbox/campaignsActions
 * @param {CampaignsParams} [params={}] - Parameters for the query of campaigns.
 * @returns {Function} Function thunk for dispatch and getState.
 * @throws {Error} If there is an error in the query to the model.
 * @example
 * dispatch(fetchCampaignsIfEmpty({ status: 'active' }));
 * @see CampaignsModelMC.getCampaigns
 */
export const fetchCampaignsIfEmpty = (params = {}) => async (dispatch, getState) => {
  const { campaigns, loadingCampaigns } = getState().mainReducer || {};
  if (loadingCampaigns) return;
  if (!campaigns || !campaigns.length) {
    dispatch({ type: "START_CAMPAIGNS_REQUEST" });
    try {
      const { data } = await CampaignsModelMC.getCampaigns(params);
      dispatch({ type: "SET_CAMPAIGNS", data: { campaigns: data } });
    } catch (err) {
      dispatch({ type: "SET_CAMPAIGNS_ERROR", err });
    }
  } else {
    dispatch({ type: "CAMPAIGNS_ALREADY_LOADED" });
  }
};
